import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { get } from '../api/client.js';
import { SlideView, useSlideShow, type BoardSlide } from '../components/BoardSlides.js';

interface DisplayDay {
  server_time: string;
  country: string;
  ministry: string;
  directorate: string;
  school: { name: string; timezone: string; principal: string | null; deputy: string | null };
  date: string;
  holiday: string | null;
  schedule_name: string | null;
  shifts: Array<{
    name: string;
    start: string;
    end: string;
    periods: Array<{ kind: 'lesson' | 'break'; label: string; start: string; end: string }>;
  }>;
  sounds: Array<{ at: string; label: string; detail: string | null }>;
  next_day: { date: string; start: string } | null;
  device: null | {
    online: boolean;
    schedule_applied: boolean;
    phase: string | null;
    lesson: number | null;
    timer_running: boolean;
    remaining_ms: number | null;
    state_at: string | null;
    playing: string | null;
  };
  alert: { active: boolean; since: string | null };
}

const MOTTO = 'بالعلم نبني الإنسان .. وبالتربية نبني العراق';

type View = 'both' | 'schedule' | 'board';
type Theme = 'night' | 'day';
const ORDINALS = ['الأولى', 'الثانية', 'الثالثة', 'الرابعة', 'الخامسة', 'السادسة', 'السابعة', 'الثامنة'];
const ordinal = (n: number | null): string => (n && n > 0 ? (ORDINALS[n - 1] ?? String(n)) : '');

function countdown(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
}

/** Whole minutes read better than a ticking number on a wall a room away. */
function remainingWords(ms: number): string {
  const minutes = Math.round(ms / 60000);
  if (minutes <= 0) return 'أقل من دقيقة';
  if (minutes === 1) return 'متبقي دقيقة';
  if (minutes === 2) return 'متبقي دقيقتان';
  if (minutes <= 10) return `متبقي ${minutes} دقائق`;
  return `متبقي ${minutes} دقيقة`;
}

/**
 * The school's hall screen. The board keeps the left of the screen, the day
 * keeps the right, and everything on it is what the bell itself reports: the
 * lesson it is running, the clip it is playing, the siren when it sounds.
 */
export function DisplayScreen(): JSX.Element {
  const { token } = useParams();
  const [clientNow, setClientNow] = useState(() => Date.now());
  const [isFullscreen, setIsFullscreen] = useState(false);
  /* Two choices the school makes once and the screen then keeps to itself. */
  const [view, setView] = useState<View>(() => (localStorage.getItem('sd-view') as View) || 'both');
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem('sd-theme') as Theme) || 'night');

  useEffect(() => {
    try {
      localStorage.setItem('sd-view', view);
      localStorage.setItem('sd-theme', theme);
    } catch {
      /* a kiosk browser may block storage; the choice simply is not kept */
    }
  }, [view, theme]);

  useEffect(() => {
    const t = setInterval(() => setClientNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onChange = (): void => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    onChange();
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const day = useQuery({
    queryKey: ['display', token],
    queryFn: () => get<DisplayDay>(`/public/display/${token}`),
    enabled: Boolean(token),
    refetchInterval: 2_000,
    // A hall screen is never the focused window: without this the browser
    // stops polling the moment anyone clicks elsewhere, and the screen freezes.
    refetchIntervalInBackground: true,
    retry: true,
  });
  // The board changes when the school changes it, not every two seconds.
  const board = useQuery({
    queryKey: ['display-board', token],
    queryFn: () => get<{ slides: BoardSlide[] }>(`/public/display/${token}/board`),
    enabled: Boolean(token),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    retry: true,
  });

  const data = day.data;
  const tz = data?.school.timezone ?? 'Asia/Baghdad';
  const now = clientNow + (data ? Date.parse(data.server_time) - day.dataUpdatedAt : 0);

  const fmt = useMemo(() => {
    const make = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
      new Intl.DateTimeFormat('ar-IQ-u-nu-latn', { timeZone: tz, ...options });
    const hour24 = make({ hour: '2-digit', hourCycle: 'h23' });
    return {
      /*
       * Hours and minutes only: Arabic Intl appends ص/م to a 12-hour time, but
       * the screen writes the word out in full beside it, so the marker is
       * dropped here rather than printed twice.
       */
      clock: (ms: number): string =>
        make({ hour: '2-digit', minute: '2-digit', hourCycle: 'h12' })
          .formatToParts(ms)
          .filter((part) => part.type === 'hour' || part.type === 'minute' || part.type === 'literal')
          .map((part) => part.value)
          .join('')
          .trim(),
      time: make({ hour: '2-digit', minute: '2-digit', hourCycle: 'h12' }),
      weekday: make({ weekday: 'long' }),
      date: make({ day: 'numeric', month: 'long', year: 'numeric' }),
      /** Arabic wants a word, not "AM": صباحًا before noon, مساءً after. */
      period: (ms: number): string => (Number(hour24.format(ms)) < 12 ? 'صباحًا' : 'مساءً'),
    };
  }, [tz]);

  const periods = useMemo(() => {
    const out: Array<{
      kind: 'lesson' | 'break';
      label: string;
      shift: string;
      number: number;
      startMs: number;
      endMs: number;
    }> = [];
    for (const shift of data?.shifts ?? []) {
      let lesson = 0;
      for (const p of shift.periods) {
        if (p.kind === 'lesson') lesson += 1;
        out.push({
          kind: p.kind,
          label: p.label,
          shift: shift.name,
          number: lesson,
          startMs: Date.parse(p.start),
          endMs: Date.parse(p.end),
        });
      }
    }
    return out;
  }, [data]);
  const lessons = useMemo(() => periods.filter((p) => p.kind === 'lesson'), [periods]);
  const sounds = useMemo(() => (data?.sounds ?? []).map((s) => ({ ...s, atMs: Date.parse(s.at) })), [data]);

  // A clip is often shorter than the gap between polls: hold the last one briefly.
  const lastClip = useRef<{ name: string; at: number } | null>(null);
  useEffect(() => {
    const playing = day.data?.device?.playing;
    if (playing) lastClip.current = { name: playing, at: Date.now() };
  }, [day.data?.device?.playing]);

  // The countdown runs off a local anchor and is only re-set when it has really
  // drifted, so a late or early poll never makes the number jump about.
  const anchor = useRef<{ value: number; at: number } | null>(null);
  const steady = (target: number | null): number | null => {
    if (target === null) {
      anchor.current = null;
      return null;
    }
    const nowMs = performance.now();
    const current = anchor.current;
    const predicted = current ? current.value - (nowMs - current.at) : null;
    if (predicted === null || Math.abs(predicted - target) > 1_500) {
      anchor.current = { value: target, at: nowMs };
      return target;
    }
    return Math.max(0, predicted);
  };

  const slides = board.data?.slides ?? [];
  const slideIndex = useSlideShow(slides);
  const slide = slides[slideIndex];

  /* The moving marker: it slides to the lesson that is running and the list
     scrolls under it, so the day can be longer than the panel is tall. */
  const viewRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [marker, setMarker] = useState({ top: 0, height: 0, shown: false });
  const [scroll, setScroll] = useState(0);
  const stickyRow = useRef(-1);

  const toggleFullscreen = (): void => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.().catch(() => undefined);
  };

  if (day.isLoading) {
    return (
      <div className="sd sd-center">
        <div className="sd-msg">جارٍ التحميل…</div>
      </div>
    );
  }
  if (day.error || !data) {
    return (
      <div className="sd sd-center">
        <div className="sd-msg">هذا الرابط غير صالح أو أُوقف. اطلب رابطًا جديدًا من إدارة المديرية.</div>
      </div>
    );
  }

  const alarm = data.alert.active;
  const device = data.device;
  const deviceRunning = Boolean(device?.online && (device.phase === 'lesson' || device.phase === 'break'));
  const recent = lastClip.current;
  const playingClip = device?.playing ?? (recent && clientNow - recent.at < 8_000 ? recent.name : null);

  // Which period the screen is about: what the device says, else the clock.
  let activeIndex = -1;
  if (deviceRunning && device) {
    const wanted = device.phase === 'lesson' ? 'lesson' : 'break';
    const matches = periods
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => p.kind === wanted && p.number === (device.lesson ?? 0));
    const near = matches.find(({ p }) => now >= p.startMs - 3_600_000 && now <= p.endMs + 3_600_000);
    activeIndex = (near ?? matches[0])?.i ?? -1;
  }
  if (activeIndex < 0) activeIndex = periods.findIndex((p) => p.startMs <= now && now < p.endMs);
  const active = activeIndex >= 0 ? periods[activeIndex] : null;

  /*
   * The row the marker sits on: the lesson the bell is running, or the one it
   * will ring next. It is deliberately sticky — between one lesson and the next
   * the bell passes through a reading that belongs to neither, and following it
   * threw the marker across the table and back. While the bell is talking to us
   * the marker only moves to a lesson the bell has actually named; the clock is
   * used only once the bell has gone quiet.
   */
  const isBreakNow = active?.kind === 'break';
  const activeLesson = active?.kind === 'lesson' ? active : null;
  let markerRow = -1;
  if (deviceRunning && activeLesson) {
    markerRow = lessons.findIndex((l) => l.startMs === activeLesson.startMs);
  } else if (deviceRunning && isBreakNow) {
    markerRow = lessons.findIndex((l) => l.number === (device?.lesson ?? 0) + 1);
  } else if (device?.online) {
    markerRow = stickyRow.current;
  } else {
    markerRow = activeLesson
      ? lessons.findIndex((l) => l.startMs === activeLesson.startMs)
      : lessons.findIndex((l) => l.startMs > now);
  }
  if (markerRow >= 0 && markerRow < lessons.length) stickyRow.current = markerRow;
  else markerRow = -1;

  const upcoming = periods.find((p) => p.startMs > now) ?? null;
  /*
   * The break to come is the one after the lesson the bell is in, not the next
   * one on the clock: while the bell runs a shortened or simulated day the two
   * disagree, and the screen has to say what the bell will actually ring.
   */
  const nextBreak = (() => {
    if (deviceRunning && device) {
      const after = device.phase === 'lesson' ? (device.lesson ?? 0) : (device.lesson ?? 0) + 1;
      return periods.find((p) => p.kind === 'break' && p.number === after) ?? null;
    }
    return periods.find((p) => p.kind === 'break' && p.startMs > now) ?? null;
  })();
  const nextSound = sounds.find((s) => s.atMs > now) ?? null;
  const first = periods[0];

  let headline = '';
  let times: string | null = null;
  let target: number | null = null;
  let progress: number | null = null;
  let badge: string | null = null;

  if (deviceRunning && device) {
    headline =
      device.phase === 'lesson' ? `الحصة ${ordinal(device.lesson)}` : `استراحة بعد الحصة ${ordinal(device.lesson)}`;
    badge = 'الآن';
    if (active) times = `${fmt.time.format(active.startMs)} — ${fmt.time.format(active.endMs)}`;
    if (device.timer_running && device.remaining_ms !== null) {
      const since = device.state_at ? now - Date.parse(device.state_at) : 0;
      target = Math.max(0, device.remaining_ms - since);
    } else if (active) {
      target = active.endMs - now;
    }
  } else if (device?.online && device.phase === 'finished') {
    headline = 'انتهى دوام اليوم';
    times = data.next_day ? `الدوام القادم ${fmt.weekday.format(Date.parse(data.next_day.start))}` : null;
  } else if (data.holiday) {
    headline = `عطلة: ${data.holiday}`;
  } else if (!first) {
    headline = 'لا يوجد دوام اليوم';
  } else if (active) {
    headline = active.label;
    badge = 'الآن';
    times = `${fmt.time.format(active.startMs)} — ${fmt.time.format(active.endMs)}`;
    target = active.endMs - now;
    progress = ((now - active.startMs) / (active.endMs - active.startMs)) * 100;
  } else if (now < first.startMs) {
    headline = 'قبل بدء الدوام';
    times = `يبدأ ${fmt.time.format(first.startMs)} ${fmt.period(first.startMs)}`;
    target = first.startMs - now;
  } else if (upcoming) {
    headline = 'بين الدوامين';
    times = `يبدأ «${upcoming.shift}» ${fmt.time.format(upcoming.startMs)}`;
    target = upcoming.startMs - now;
  } else {
    headline = 'انتهى دوام اليوم';
    times = data.next_day ? `الدوام القادم ${fmt.weekday.format(Date.parse(data.next_day.start))}` : null;
  }

  const remaining = steady(target);

  /*
   * The marker belongs to a lesson in progress. While a bell or an announcement
   * is playing, and outside the lessons altogether, there is nothing for it to
   * point at — so it steps back rather than sitting on a row that is not the
   * one the school is in.
   */
  const showMarker = markerRow >= 0 && !playingClip && (deviceRunning || active !== null);

  /*
   * The bar measures what is left of the period against how long the period is.
   * Reading it off the clock was wrong whenever the bell's day and the calendar
   * day were not the same minute — a shortened day, a simulation, a late start.
   */
  if (deviceRunning && active && remaining !== null) {
    const span = active.endMs - active.startMs;
    progress = span > 0 ? ((span - remaining) / span) * 100 : null;
  }

  return (
    <div className={`sd ${theme === 'day' ? 'day' : ''} ${alarm ? 'alarm' : ''}`}>
      <Marker
        viewRef={viewRef}
        listRef={listRef}
        rowRefs={rowRefs}
        row={showMarker ? markerRow : -1}
        count={lessons.length}
        onMeasure={(m, s) => {
          setMarker(m);
          setScroll(s);
        }}
      />

      <header className="sd-head">
        <div className="sd-clockwrap">
          <div className="sd-clock">
            <span className="num" dir="ltr">
              {fmt.clock(now)}
            </span>
            <span className="sd-mer">{fmt.period(now)}</span>
          </div>
          <div className="sd-date">
            {fmt.weekday.format(now)}، {fmt.date.format(now)}
          </div>
        </div>

        <div className="sd-ministry">
          <div className="sd-state">
            {data.country}
            <span className="sd-sep">|</span>
            {data.ministry}
          </div>
          <div className="sd-emblem-line">
            <img className="sd-emblem" src="/emblem-iraq.svg" alt="شعار جمهورية العراق" />
          </div>
          <div className="sd-motto">{MOTTO}</div>
        </div>

        <div className="sd-school">
          <h1>{data.school.name}</h1>
          <p>{data.directorate}</p>
        </div>
      </header>

      <div className={`sd-layout view-${alarm ? 'both' : view}`}>
        <section className={`sd-board ${alarm || view === 'schedule' ? 'away' : ''}`} aria-label="لوحة الإعلانات">
          {
            <>
              <div className="sd-board-top">
                <span className="sd-board-title">لوحة الإعلانات</span>
                {slides.length > 1 ? (
                  <span className="sd-counter num" dir="ltr">
                    {String(slideIndex + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                  </span>
                ) : null}
              </div>

              <div className="sd-slide" key={slide?.id ?? 'empty'}>
                {slide ? (
                  <SlideView slide={slide} />
                ) : (
                  <div className="bd-chalk">
                    <h2>لوحة إعلانات المدرسة</h2>
                  </div>
                )}
              </div>

              <div className="sd-chalks" aria-hidden="true">
                <i />
                <i />
                <span className="sd-eraser" />
              </div>

              {slides.length > 1 ? (
                <nav className="sd-pager" aria-hidden="true">
                  {slides.map((s, i) => (
                    <i key={s.id} className={i === slideIndex ? 'on' : ''} />
                  ))}
                </nav>
              ) : null}
            </>
          }
        </section>

        <aside className={`sd-side ${alarm || view === 'board' ? 'away' : ''}`}>
          <section className="sd-panel sd-current" aria-label="الحصة الحالية">
            <div className="sd-current-top">
              <svg className="sd-book" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 5C9 2 4 3 2 4v16c4-2 7-1 10 1 3-2 6-3 10-1V4c-2-1-7-2-10 1Zm0 0v16" strokeLinejoin="round" />
              </svg>
              <div>
                <h2>{headline}</h2>
                {times ? (
                  <div className="sd-times num" dir="ltr">
                    {times}
                  </div>
                ) : null}
              </div>
            </div>
            {badge ? (
              <div className="sd-badge">
                <span className="sd-dot" />
                {badge}
              </div>
            ) : null}
            {progress !== null ? (
              <div className="sd-track">
                <i style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
              </div>
            ) : null}
            {remaining !== null ? (
              <div className="sd-remaining">
                {remainingWords(remaining)}
                <span className="sd-remaining-exact num" dir="ltr">
                  {countdown(remaining)}
                </span>
              </div>
            ) : null}
          </section>

          <section className="sd-panel sd-schedule" aria-label="جدول اليوم">
            <div className="sd-schedule-title">
              <svg className="sd-cal" viewBox="0 0 24 24" aria-hidden="true">
                <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6.5h16a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 20V8A1.5 1.5 0 0 1 4 6.5Z" />
                  <path d="M2.5 11h19M8 3v5M16 3v5" />
                  <path d="M7 15h2M11 15h2M15 15h2M7 18.5h2M11 18.5h2" />
                </g>
              </svg>
              جدول اليوم
            </div>

            <div className="sd-rows" ref={viewRef}>
              <div className="sd-rows-list" ref={listRef} style={{ transform: `translateY(-${scroll}px)` }}>
                {marker.shown ? (
                  <div
                    className={`sd-marker ${isBreakNow ? 'soon' : ''}`}
                    style={{ transform: `translateY(${marker.top}px)`, height: `${marker.height}px` }}
                    aria-hidden="true"
                  />
                ) : null}
                {lessons.length === 0 ? (
                  <div className="sd-empty">{data.holiday ? `عطلة: ${data.holiday}` : 'لا توجد حصص اليوم'}</div>
                ) : null}
                {lessons.map((l, i) => {
                  const state = i === markerRow ? 'on' : l.endMs <= now ? 'past' : 'future';
                  return (
                    <div
                      key={`${l.startMs}-${i}`}
                      className={`sd-row ${state}`}
                      ref={(el) => {
                        rowRefs.current[i] = el;
                      }}
                    >
                      <span className="sd-num num" dir="ltr">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="sd-name">{l.label}</span>
                      <span className="sd-time num" dir="ltr">
                        {fmt.time.format(l.startMs)} — {fmt.time.format(l.endMs)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sd-break">
              <strong className="num" dir="ltr">
                {nextBreak ? fmt.time.format(nextBreak.startMs) : nextSound ? fmt.time.format(nextSound.atMs) : '—'}
              </strong>
              <span>{nextBreak ? 'الاستراحة القادمة' : nextSound ? nextSound.label : 'لا جرس قادم اليوم'}</span>
              <svg className="sd-cup" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 15h23v11c0 13-23 13-23 0Zm23 2h4c8 0 6 12-4 11M13 10c-5-5 4-5 0-10m8 10c-5-5 4-5 0-10" strokeLinecap="round" />
              </svg>
            </div>
          </section>

          {/* The player is here only while something is actually playing: the
              screen has no buttons because nobody can press them. */}
          {playingClip ? (
            <section className="sd-panel sd-audio playing" aria-label="الصوت">
              <div className="sd-speaker">
                <svg viewBox="0 0 24 24">
                  <path d="M3 9h4l5-4v14l-5-4H3z" fill="currentColor" />
                  <path d="M16 8c3 2 3 6 0 8m3-11c5 4 5 10 0 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </div>
              <div className="sd-audio-copy">
                <div className="sd-playing-label">
                  <span className="sd-dot" />
                  يُشغَّل الآن
                </div>
                <strong>{playingClip}</strong>
              </div>
              <div className="sd-wave" aria-hidden="true">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <i key={i} style={{ animationDelay: `${i * 0.09}s`, height: `${44 + ((i * 5) % 4) * 14}%` }} />
                ))}
              </div>
            </section>
          ) : null}
        </aside>

        {/* Both halves slide off, and the warning takes the whole screen. */}
        {alarm ? (
          <div className="sd-evac" role="alert">
            <svg className="sd-evac-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M12 4.5 2.8 20h18.4z" strokeLinejoin="round" />
              <path d="M12 10v4.6M12 17.5v.2" strokeLinecap="round" />
            </svg>
            <div className="sd-evac-title">إنذار طوارئ</div>
            <div className="sd-evac-text">
              غادروا القاعات بهدوء إلى نقاط التجمع، واتبعوا تعليمات المعلمين وإدارة المدرسة.
            </div>
          </div>
        ) : null}
      </div>

      {alarm ? <div className="sd-hazard" aria-hidden="true" /> : null}

      {!isFullscreen ? (
        <div className="sd-tools">
          <div className="sd-seg" role="group" aria-label="ما يُعرض">
            <button className={view === 'schedule' ? 'on' : ''} onClick={() => setView('schedule')}>
              الجدول
            </button>
            <button className={view === 'both' ? 'on' : ''} onClick={() => setView('both')}>
              الاثنان
            </button>
            <button className={view === 'board' ? 'on' : ''} onClick={() => setView('board')}>
              اللوحة
            </button>
          </div>
          <button onClick={() => setTheme(theme === 'day' ? 'night' : 'day')} title="الوضع النهاري والليلي">
            {theme === 'day' ? '🌙 ليلي' : '☀️ نهاري'}
          </button>
          <button onClick={toggleFullscreen} title="ملء الشاشة">
            ⛶ ملء الشاشة
          </button>
        </div>
      ) : null}

      {device && !device.online ? (
        <div className="sd-foot">الجهاز غير متصل الآن؛ الأوقات المعروضة من الجدول المحفوظ فيه.</div>
      ) : null}
    </div>
  );
}

/**
 * Measures where the marker should sit and how far the list must scroll for the
 * active lesson to stay in sight. It renders nothing: it exists so the measuring
 * happens after the rows are laid out, on every change that can move them.
 */
function Marker({
  viewRef,
  listRef,
  rowRefs,
  row,
  count,
  onMeasure,
}: {
  viewRef: React.RefObject<HTMLDivElement>;
  listRef: React.RefObject<HTMLDivElement>;
  rowRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  row: number;
  count: number;
  onMeasure: (marker: { top: number; height: number; shown: boolean }, scroll: number) => void;
}): null {
  useLayoutEffect(() => {
    const view = viewRef.current;
    const list = listRef.current;
    const element = row >= 0 ? rowRefs.current[row] : null;
    if (!view || !list || !element) {
      onMeasure({ top: 0, height: 0, shown: false }, 0);
      return;
    }
    const measure = (): void => {
      const top = element.offsetTop;
      const height = element.offsetHeight;
      const viewHeight = view.clientHeight;
      const listHeight = list.scrollHeight;
      // Keep the marked row in view, and one row of context under it.
      const wanted = top + height + height * 0.6 - viewHeight;
      const scroll = Math.max(0, Math.min(Math.max(0, listHeight - viewHeight), wanted));
      onMeasure({ top, height, shown: true }, scroll);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(view);
    observer.observe(list);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, count]);
  return null;
}
